import { ArrowUpRight, Bookmark, Check, Copy, GitBranch, Link2, Map, Pin, Scale, X } from 'lucide-react';
import { dataset, nodeDegree, relationsFor } from '../data/graph';
import type { GraphNode } from '../data/types';
import { dateLabel } from '../lib/selectors';
import { SourceList } from './SourceList';

const typeLabels: Record<GraphNode['type'], string> = {
  company: 'przedsiębiorstwo', person: 'osoba', architecture: 'architektura', place: 'miejsce', machine: 'maszyna', technology: 'technologia', process: 'proces', textile: 'tkanina / produkt', document: 'dokument', photograph: 'fotografia', map: 'mapa', event: 'wydarzenie', trade: 'handel', heritage: 'transformacja / dziedzictwo',
};

const confidenceLabels = { high: 'wysoka', medium: 'średnia', low: 'niska' };

interface InspectorProps {
  node?: GraphNode;
  pinned: boolean;
  compare: boolean;
  onClose: () => void;
  onPin: () => void;
  onCompare: () => void;
  onSelect: (id: string) => void;
  onPath: (id: string) => void;
  onAtlas?: () => void;
  pathIds: string[];
}

export function Inspector({ node, pinned, compare, onClose, onPin, onCompare, onSelect, onPath, onAtlas, pathIds }: InspectorProps): JSX.Element {
  if (!node) return <aside className="inspector empty-inspector"><div className="empty-mark">+</div><p>Wybierz węzeł na scenie lub z listy wyników.</p><span className="muted">Kliknięcie przenosi napięcie na lokalny splot relacji.</span></aside>;
  const relations = relationsFor(node.id);
  return (
    <aside className="inspector" aria-label="Inspektor obiektu">
      <div className="inspector-header">
        <div><span className="eyebrow">INSPEKTOR / {node.id}</span><h2>{node.title}</h2></div>
        <button className="icon-button" onClick={onClose} aria-label="Zamknij inspektor"><X size={16} /></button>
      </div>
      <div className="inspector-meta"><span className="type-chip">{typeLabels[node.type]}</span><span>{dateLabel(node)}</span><span className={`confidence confidence-${node.confidence}`}>{confidenceLabels[node.confidence]} pewność</span></div>
      <p className="inspector-summary">{node.summary}</p>
      <div className="curatorial-note"><span className="section-label">NOTA KURATORSKA</span><p>{node.curatorialNote}</p></div>
      <div className="inspector-actions">
        <button className={`tool-button ${pinned ? 'is-active' : ''}`} onClick={onPin}><Pin size={14} />{pinned ? 'Przypięty' : 'Przypnij'}</button>
        <button className={`tool-button ${compare ? 'is-active' : ''}`} onClick={onCompare}><Scale size={14} />{compare ? 'W porównaniu' : 'Porównaj'}</button>
        {onAtlas && <button className="tool-button" onClick={onAtlas}><Map size={14} />Pokaż w Atlasie</button>}
        <button className="tool-button" onClick={() => { void navigator.clipboard?.writeText(window.location.href); }}><Link2 size={14} />Kopiuj link</button>
      </div>
      {node.mediaIds && node.mediaIds.length > 0 && <div className="inspector-media">
        {node.mediaIds.map((id) => {
          const media = dataset.media.find((item) => item.id === id);
          return media?.localPath ? <figure key={id}><img src={media.localPath} alt={media.alt} /><figcaption>{media.credit}</figcaption></figure> : null;
        })}
      </div>}
      <div className="inspector-section"><div className="section-heading"><span className="section-label">RELACJE</span><span className="count-badge">{relations.length}</span></div>
        <div className="relation-list">{relations.map((relation) => {
          const otherId = relation.from === node.id ? relation.to : relation.from;
          const other = dataset.nodes.find((item) => item.id === otherId);
          if (!other) return null;
          const pathActive = pathIds.includes(relation.id);
          return <div className={`relation-row ${pathActive ? 'is-path' : ''}`} key={relation.id}>
            <button className="relation-main" onClick={() => onSelect(other.id)}><span className="relation-type">{relation.type}</span><span className="relation-label">{relation.from === node.id ? relation.label : 'związany z'}</span><span className="relation-target">{other.title}</span>{relation.dateRange && <time className="relation-date">{relation.dateRange.start}{relation.dateRange.end && relation.dateRange.end !== relation.dateRange.start ? `—${relation.dateRange.end}` : ''}</time>}<ArrowUpRight size={13} /></button>
            <button className={`relation-path ${pathActive ? 'is-active' : ''}`} onClick={() => onPath(relation.id)} aria-label={pathActive ? 'Usuń relację ze ścieżki' : 'Dodaj relację do ścieżki'}>{pathActive ? <Check size={12} /> : <GitBranch size={12} />}</button>
          </div>;
        })}</div>
      </div>
      <div className="inspector-section"><div className="section-heading"><span className="section-label">ŹRÓDŁA I PRAWA</span><Copy size={13} /></div><SourceList sourceIds={node.sourceIds} mediaIds={node.mediaIds} /></div>
      <div className="inspector-footnote"><Bookmark size={13} /> Stopień węzła: {nodeDegree(node.id)} · Pewność: {node.confidenceNote}</div>
    </aside>
  );
}
